import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

export type PriceLookupMeta = {
  isInitializing: boolean;
};

export type PriceLookupUpdateRow<Row> = (rowUuid: string, fields: Partial<Row>) => void;

export type PriceLookupHandler<Row> = (
  row: Row,
  field: string,
  value: any,
  updateRow: PriceLookupUpdateRow<Row>,
  meta?: PriceLookupMeta
) => void;

interface PriceLookupValues<Row> {
  rowUuid: string;
  itemUuid?: string;
  unit?: string;
  quantity: number;
  customerUuid?: string;
  row: Row;
  field: string;
  value: any;
}

interface BuildRequestParams<Row> extends PriceLookupValues<Row> {
  effectiveDate?: string;
}

interface BuildUpdatesParams<Row, Response> extends PriceLookupValues<Row> {
  response: Response;
}

export interface UsePriceLookupOptions<Row extends Record<string, any>, Response extends Record<string, any>> {
  endpoint: string | ((entitySlug: string) => string);
  effectiveDate?: string;
  customerUuid?: string;
  extraKey?: string;
  triggerFields?: string[];
  debounceFields?: string[];
  manualPriceFields?: string[];
  debounceMs?: number;
  buildRequest?: (params: BuildRequestParams<Row>) => Record<string, any>;
  buildUpdates: (params: BuildUpdatesParams<Row, Response>) => Partial<Row>;
}

const DEFAULT_TRIGGER_FIELDS = ['ma_vt_data', 'dvt', 'so_luong'];
const DEFAULT_DEBOUNCE_FIELDS = ['so_luong'];
const DEFAULT_MANUAL_PRICE_FIELDS = ['gia_nt'];
const DEFAULT_DEBOUNCE_MS = 400;

const formatDateOnly = (value?: string) => (value ? value.split('T')[0] : undefined);

const resolveEndpoint = (endpoint: UsePriceLookupOptions<any, any>['endpoint'], entitySlug: string) =>
  typeof endpoint === 'function' ? endpoint(entitySlug) : `/entities/${entitySlug}${endpoint}`;

const resolveLookupValues = <Row extends Record<string, any>>(
  row: Row,
  field: string,
  value: any,
  customerUuid?: string
): PriceLookupValues<Row> => {
  const maVtData = field === 'ma_vt_data' ? value : row.ma_vt_data;

  return {
    rowUuid: String(row.uuid || ''),
    itemUuid: maVtData?.uuid,
    unit: field === 'dvt' ? value : row.dvt || maVtData?.dvt_nl || maVtData?.dvt,
    quantity: field === 'so_luong' ? Number(value ?? 0) : Number(row.so_luong ?? 0),
    customerUuid,
    row,
    field,
    value
  };
};

const buildLookupKey = <Row extends Record<string, any>>(
  values: PriceLookupValues<Row>,
  effectiveDate?: string,
  extraKey?: string
) =>
  [
    values.itemUuid || '',
    values.unit || '',
    values.quantity,
    values.customerUuid || '',
    effectiveDate || '',
    extraKey || ''
  ].join('|');

export function usePriceLookup<Row extends Record<string, any>, Response extends Record<string, any>>(
  options: UsePriceLookupOptions<Row, Response>
): PriceLookupHandler<Row> {
  const { entity } = useAuth();
  const optionsRef = useRef(options);
  const fetchedKeysRef = useRef(new Map<string, string>());
  const timeoutRefsRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const isInitializingRef = useRef(true);

  optionsRef.current = options;

  useEffect(() => {
    isInitializingRef.current = false;

    return () => {
      timeoutRefsRef.current.forEach(timeout => clearTimeout(timeout));
      timeoutRefsRef.current.clear();
    };
  }, []);

  const fetchPrice = useCallback(
    async (values: PriceLookupValues<Row>, updateRow: PriceLookupUpdateRow<Row>) => {
      if (!entity?.slug || !values.rowUuid || !values.itemUuid || !values.unit) return;
      const currentOptions = optionsRef.current;

      const request = currentOptions.buildRequest?.({ ...values, effectiveDate: currentOptions.effectiveDate }) ?? {
        ma_vt: values.itemUuid,
        dvt: values.unit,
        so_luong: String(values.quantity ?? 0),
        ...(values.customerUuid ? { ma_kh: values.customerUuid } : {}),
        ...(currentOptions.effectiveDate ? { ngay_hl: formatDateOnly(currentOptions.effectiveDate) } : {})
      };

      try {
        const response = await api.post<Response>(resolveEndpoint(currentOptions.endpoint, entity.slug), request);
        const updates = currentOptions.buildUpdates({ ...values, response: response.data });
        updateRow(values.rowUuid, updates);
      } catch {
        // Price lookup is best-effort; row editing should continue when the lookup endpoint fails.
      }
    },
    [entity?.slug]
  );

  const queuePriceLookup = useCallback(
    (values: PriceLookupValues<Row>, debounce: boolean, updateRow: PriceLookupUpdateRow<Row>) => {
      const currentOptions = optionsRef.current;
      const currentKey = buildLookupKey(values, currentOptions.effectiveDate, currentOptions.extraKey);
      const previousKey = fetchedKeysRef.current.get(values.rowUuid);

      if (currentKey === previousKey) return;

      const existingTimeout = timeoutRefsRef.current.get(values.rowUuid);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        timeoutRefsRef.current.delete(values.rowUuid);
      }

      const run = () => {
        void fetchPrice(values, updateRow);
        fetchedKeysRef.current.set(values.rowUuid, currentKey);
        timeoutRefsRef.current.delete(values.rowUuid);
      };

      if (debounce) {
        timeoutRefsRef.current.set(values.rowUuid, setTimeout(run, currentOptions.debounceMs ?? DEFAULT_DEBOUNCE_MS));
        return;
      }

      run();
    },
    [fetchPrice]
  );

  return useCallback(
    (row: Row, field: string, value: any, updateRow: PriceLookupUpdateRow<Row>, meta?: PriceLookupMeta) => {
      if (meta?.isInitializing || isInitializingRef.current) return;
      if (!entity?.slug || !row.uuid) return;
      const currentOptions = optionsRef.current;
      const currentManualPriceFields = currentOptions.manualPriceFields ?? DEFAULT_MANUAL_PRICE_FIELDS;
      const currentTriggerFields = currentOptions.triggerFields ?? DEFAULT_TRIGGER_FIELDS;
      const currentDebounceFields = currentOptions.debounceFields ?? DEFAULT_DEBOUNCE_FIELDS;

      if (currentManualPriceFields.includes(field)) {
        fetchedKeysRef.current.delete(String(row.uuid));
        return;
      }

      if (!currentTriggerFields.includes(field)) return;

      const values = resolveLookupValues(row, field, value, currentOptions.customerUuid);

      if (!values.itemUuid || !values.unit) {
        fetchedKeysRef.current.delete(values.rowUuid);
        const existingTimeout = timeoutRefsRef.current.get(values.rowUuid);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
          timeoutRefsRef.current.delete(values.rowUuid);
        }
        return;
      }

      queuePriceLookup(values, currentDebounceFields.includes(field), updateRow);
    },
    [entity?.slug, queuePriceLookup]
  );
}
